import React, { useCallback, useEffect, useRef } from 'react';
import T from 'prop-types';
import { isEmpty, some } from 'lodash-es';

import WarningNotification from '../WarningNotification';
import DateRangePicker from '../../../DateRangePicker';
import { maxDate as decorateMaxDate } from '../../../DateRangePicker/configs';
import PredefinedRangesDropdownWrapper from '../../../DateRangePicker/components/PredefinedRangesDropdownWrapper';

import { DATA_TYPE, ADVANCED_METRIC_MIN_DATE, MPI_MIN_DATE } from '../../configs/constants';
import { today, format, differenceInDays, subDays, isBefore, ISODateFormat } from '../../../../utils/date';

const DateRange = ({
  initParams,
  dataType,
  report,
  disabled,
  onReportChange,
}) => {
  const isDateSetRef = useRef(false);

  const { maxDate, advancedMaxDate, maxPeriod: initMaxPeriod } = initParams;

  const maxDateTo = (() => {
    if (dataType === DATA_TYPE.ADVANCED && advancedMaxDate) {
      return decorateMaxDate(advancedMaxDate);
    }

    return decorateMaxDate(maxDate);
  })();

  const maxPeriod = (() => {
    let maxPeriodValue = initMaxPeriod;

    if (dataType === DATA_TYPE.ADVANCED) {
      const advancedMaxPeriod = differenceInDays(today(), ADVANCED_METRIC_MIN_DATE);
      if (initMaxPeriod > advancedMaxPeriod) maxPeriodValue = advancedMaxPeriod;
    }

    if (dataType === DATA_TYPE.MPI) {
      const mpiMaxPeriod = differenceInDays(today(), MPI_MIN_DATE);
      if (initMaxPeriod > mpiMaxPeriod) maxPeriodValue = mpiMaxPeriod;
    }

    return maxPeriodValue;
  })();

  const currentPeriod = (report.end_date && report.start_date)
    ? differenceInDays(report.end_date, report.start_date)
    : 1;

  const showWarning = (() => {
    let dateWarning = {
      show: some([report.date_from, report.date_to], isEmpty) && isDateSetRef.current,
      message: 'You must select a starting and ending date for your export.',
    };

    if (dataType === DATA_TYPE.ADVANCED) {
      if (isBefore(report.start_date, ADVANCED_METRIC_MIN_DATE)) {
        dateWarning = {
          show: true,
          message: `The start date must be after ${format(ADVANCED_METRIC_MIN_DATE)}.`,
        };
      }
    }

    if (dataType === DATA_TYPE.MPI) {
      if (isBefore(report.start_date, MPI_MIN_DATE)) {
        dateWarning = {
          show: true,
          message: `The start date must be after ${format(MPI_MIN_DATE)}.`,
        };
      }
    }

    return dateWarning;
  })();

  const handleDateChange = useCallback(({ period_from_date, period_to_date, period }) => {
    onReportChange(prevState => ({
      ...prevState,
      start_date: period_from_date
        ? format(period_from_date, ISODateFormat)
        : subDays(period_to_date, period - 1),
      end_date: format(period_to_date, ISODateFormat),
      disabled: (!isEmpty(period_from_date) && !isEmpty(period_to_date) && prevState.disabled)
        ? false
        : prevState.disabled,
    }));
  }, []);

  // Effects
  useEffect(() => {
    if (report.date_from && report.date_to) {
      isDateSetRef.current = true;
    }
  }, [report.date_from, report.date_to]);

  return (
    <section className="upsert-export-form__date-range">
      {disabled && <div className="disabled-block" />}
      <div className="date-range-section__about">
        <h4 className="h5 date-range-section__title">Date range</h4>
        <div className="date-range-section__date-pickers">
          <PredefinedRangesDropdownWrapper
            dateFrom={report.start_date}
            dateTo={report.end_date}
            currentPeriod={currentPeriod}
            maxDateTo={maxDateTo}
            maxPeriod={maxPeriod}
            maxShowedPeriod={maxPeriod}
            onChange={handleDateChange}
          />
          <DateRangePicker
            currentPeriod={currentPeriod}
            dateFrom={report.start_date}
            dateTo={report.end_date}
            maxPeriod={maxPeriod}
            maxDateTo={maxDateTo}
            maxShowedPeriod={maxPeriod}
            onChange={handleDateChange}
          />
        </div>
      </div>
      {showWarning.show && (
        <div className="date-range-section__content">
          <WarningNotification text={showWarning.message} />
        </div>
      )}
    </section>
  );
};

DateRange.propTypes = {
  dataType: T.string.isRequired,
  report: T.object.isRequired,
  initParams: T.object.isRequired,
  disabled: T.bool,
  onReportChange: T.func.isRequired,
};

export default DateRange;
